import { ReactElement } from 'react';
import styled, { CSSProp } from 'styled-components';
import { BackgroundLayout } from './BackgroundLayout';
import { NavigationButtonV2 } from '../NavigationButtonV2';
import { useTranslation } from 'react-i18next';

interface FormLayoutProps {
  children: React.ReactNode;
  onBack: () => void;
  onNext: () => void;
  prevLabel: string;
  nextLabel: string;
  submitLabel: string;
  activeTab: string;
  state?: boolean;
  iconStyle?: CSSProp;
  isValid: boolean;
  questionnaireType?: string;
  currentPage: number;
  totalPages: number;
  formIndex: number;
  totalFormPages: number;
  hideBackButton?: boolean;
  answeredPages?: boolean[];
  onGoToPage?: (page: number) => void;
}

const ProgressBar = styled.div<{ currentPage: number; totalPages: number }>`
  background-color: #2e4383;
  height: 5px;
  width: ${({ currentPage, totalPages }) => ((currentPage + 1) / totalPages) * 100}%;
`;

export const FormLayout = ({
  children,
  onBack,
  onNext,
  prevLabel,
  nextLabel,
  submitLabel,
  activeTab,
  state,
  iconStyle,
  isValid,
  currentPage,
  totalPages,
  formIndex,
  totalFormPages,
  questionnaireType,
  hideBackButton = false,
  answeredPages,
  onGoToPage
}: FormLayoutProps): ReactElement => {
  const { t } = useTranslation();

  const NextButton = () => {
    if (activeTab !== 'overview') {
      return (
        <NavigationButtonV2
          onPress={onNext}
          label={
            currentPage + 1 === totalPages && formIndex === totalFormPages - 1
              ? submitLabel
              : nextLabel
          }
          valid={isValid}
          disabled={!isValid}
          direction="right"
        />
      );
    }
    return null;
  };

  return (
    <BackgroundLayout
      innerStyle="padding-left: 0; padding-right: 0; padding-top: 0;"
      iconStyle={iconStyle}
      transparent={true}
      showIcon={false}>
      <div className="form-layout-container">
        <div className="header">
          <div className="headerLeft">
            {questionnaireType === 'eq' && (
              <img
                src={require('./../../assets/images/so-mds-icon.png')}
                className="logo"
                alt="SO-MDS Logo"
              />
            )}
            <div className="headerTitleContainer">
              <div className="headerTitle">{questionnaireType !== 'eq' ? questionnaireType?.toUpperCase() : 'MDS'}</div>
              {questionnaireType !== 'eq' && <div className="headerSubTitle">{t(`common:${questionnaireType}Header`)}</div>}
            </div>
          </div>
          {questionnaireType !== 'eq' && <div className="headerCenter">{t('common:oxfordHeader')}</div>}
        </div>
        <div className={`content ${state ? 'contentOut' : 'contentIn'}`}>{children}</div>
        <div className="footer">
          <div className="progressBar">
            <ProgressBar currentPage={currentPage} totalPages={totalPages} />
          </div>
          {answeredPages && answeredPages.length > 1 && (
            <div className="questionIndicators">
              {answeredPages.map((answered, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={`indicator${currentPage === idx ? ' indicator--active' : ''}${answered ? ' indicator--answered' : ''}`}
                  onClick={() => onGoToPage?.(idx)}>
                  {idx + 1}
                </button>
              ))}
            </div>
          )}
          <div className="footerContainer">
            {!hideBackButton ? (
              <NavigationButtonV2 onPress={onBack} label={prevLabel} direction="left" />
            ) : (
              <div></div>
            )}
            <NextButton />
          </div>
        </div>
      </div>
    </BackgroundLayout>
  );
};
